import React, { useState, useEffect } from 'react';
import { Close, Person, SupervisorAccount } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import {
    addUserRole,
    buildUserRoleFormPayload,
    editUserRole,
    fetchRolesForInvitation,
    resolveSolutionId,
} from '../../../services/userRoleService';

export const InviteUserModal = ({ show, onClose, onInvite, role = null }) => {
    const { selectedOrganization } = useAuth();
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        user_id: role?.user?.phone?.fullNumber || '243',
        solution_id: null,
        company_id: selectedOrganization?.id || null,
        role_id: role?.role?.id || null
    });

    // Charger les rôles disponibles
    useEffect(() => {
        if (show && selectedOrganization?.id) {
            loadRoles();
        }
    }, [show, selectedOrganization]);

    // Initialiser le formulaire
    useEffect(() => {
        if (role) {
            setForm({
                ...role,
                user_id: role?.user?.phone?.fullNumber || '243',
                solution_id: role?.solution?.id || localStorage.getItem('netikash_solution'),
                company_id: selectedOrganization?.id || role?.company?.id,
                role_id: role?.role?.id || null
            });
            setSelectedRole(role?.role || null);
        } else {
            const solutionId = localStorage.getItem('netikash_solution');
            setForm({
                user_id: '243',
                solution_id: solutionId,
                company_id: selectedOrganization?.id,
                role_id: null
            });
            setSelectedRole(null);
        }
    }, [role, selectedOrganization, show]);

    // S'assurer que user_id commence par 243
    useEffect(() => {
        if (form?.user_id === undefined || !form?.user_id.startsWith('243') || form?.user_id.startsWith('2430')) {
            setForm(prev => ({...prev, user_id: '243'}));
        }
    }, [form.user_id]);

    const loadRoles = async () => {
        try {
            const rolesList = await fetchRolesForInvitation('');
            setRoles(rolesList);
        } catch (error) {
            console.error('Error loading roles:', error);
        }
    };

    const handleSelectRole = (roleItem) => {
        setSelectedRole(roleItem);
        setForm(prev => ({...prev, role_id: roleItem.id}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.user_id || form.user_id.length < 12) {
            alert('Veuillez entrer un numéro de téléphone valide');
            return;
        }

        if (!form.role_id) {
            alert('Veuillez sélectionner un rôle');
            return;
        }

        try {
            setSubmitting(true);
            
            const solutionId = form.solution_id || (await resolveSolutionId());
            const roleData = buildUserRoleFormPayload(selectedOrganization.id, {
                user_id: form.user_id,
                role_id: form.role_id,
                solution_id: solutionId,
                userRoleId: role?.id,
            });

            let response;
            if (role?.id) {
                response = await editUserRole(selectedOrganization.id, role.id, roleData);
            } else {
                response = await addUserRole(selectedOrganization.id, roleData);
            }

            if (response) {
                onInvite(response);
                setForm({
                    user_id: '243',
                    solution_id: localStorage.getItem('netikash_solution'),
                    company_id: selectedOrganization?.id,
                    role_id: null
                });
                setSelectedRole(null);
                onClose();
            }
        } catch (error) {
            console.error('Error inviting user:', error);
            alert(error.message || 'Erreur lors de l\'invitation');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePhoneChange = (value) => {
        // Format comme sur mobile : seulement les chiffres, commence par 243
        let formattedValue = value.replace(/\D/g, '');
        if (!formattedValue.startsWith('243')) {
            if (formattedValue.startsWith('0')) {
                formattedValue = '243' + formattedValue.substring(1);
            } else if (formattedValue) {
                formattedValue = '243' + formattedValue;
            } else {
                formattedValue = '243';
            }
        }
        setForm(prev => ({...prev, user_id: formattedValue}));
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Inviter un Utilisateur</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <Close className="text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Numéro de téléphone */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Numéro de téléphone *
                        </label>
                        <input
                            type="tel"
                            required
                            value={form.user_id}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                            placeholder="990000000"
                            maxLength={12}
                            className="w-full px-4 py-3 border-2 border-amber-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                        />
                        <p className="text-gray-500 text-sm mt-1">
                            L'utilisateur doit avoir un compte Netikash actif
                        </p>
                    </div>

                    {/* Rôle */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Rôle *
                        </label>
                        <select
                            value={selectedRole?.id || ''}
                            onChange={(e) => {
                                const roleItem = roles.find(r => r.id === e.target.value);
                                if (roleItem) {
                                    handleSelectRole(roleItem);
                                }
                            }}
                            className="w-full px-4 py-3 border-2 border-amber-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                        >
                            <option value="">Sélectionner un rôle</option>
                            {roles.map((roleItem) => (
                                <option key={roleItem.id} value={roleItem.id}>
                                    {roleItem.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedRole && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <div className="flex items-center space-x-2 text-amber-700">
                                <div className="text-sm">
                                    <p className="font-medium">Rôle sélectionné:</p>
                                    <p className="text-amber-600 mt-1">{selectedRole.name}</p>
                                    {selectedRole.description && (
                                        <p className="text-amber-500 mt-1">{selectedRole.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex space-x-4 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-4 bg-gray-500/10 text-gray-700 rounded-xl hover:bg-gray-500/20 transition-all duration-300 border border-gray-300/50 font-semibold"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || !form.user_id || !form.role_id}
                            className="flex-1 px-6 py-4 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-all duration-300 shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                            {submitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>EN COURS...</span>
                                </>
                            ) : (
                                <span>{role?.id ? 'CONFIRMER' : 'ENVOYER L\'INVITATION'}</span>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};